import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Grid,
  Autocomplete,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { employeesApi, clientsApi } from '../../services/api';

function ProjectForm({ project, parameters, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    required_skills: [],
    parameters: {},
    client_id: null,
    client_acronym: '',
    gitlab_url: '',
    owner_id: null,
    estimated_effort_hours: '',
  });

  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    employeesApi.getAll().then((r) => setEmployees(r.data.filter((e) => e.is_active))).catch(() => {});
    clientsApi.getAll().then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const [availableSkills] = useState([
    'JavaScript', 'Python', 'React', 'Node.js', 'Flask', 'Django',
    'HTML', 'CSS', 'SQL', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker',
    'Git', 'TypeScript', 'Vue.js', 'Angular', 'Express.js', 'FastAPI',
  ]);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        status: project.status || 'active',
        required_skills: project.required_skills || [],
        parameters: project.parameters || {},
        client_id: project.client_id || null,
        client_acronym: project.client_acronym || '',
        gitlab_url: project.gitlab_url || '',
        owner_id: project.owner_id || null,
        estimated_effort_hours: project.estimated_effort_hours ?? '',
      });
    } else {
      const defaultParams = {};
      parameters.forEach((param) => {
        switch (param.data_type) {
          case 'integer':
          case 'float':
            defaultParams[param.name] = param.min_value || 1;
            break;
          case 'boolean':
            defaultParams[param.name] = false;
            break;
          case 'select':
            defaultParams[param.name] = param.options[0] || '';
            break;
          default:
            defaultParams[param.name] = '';
        }
      });
      setFormData((prev) => ({ ...prev, parameters: defaultParams }));
    }
  }, [project, parameters]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleParameterChange = (paramName, value) => {
    setFormData((prev) => ({
      ...prev,
      parameters: { ...prev.parameters, [paramName]: value },
    }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      alert('Project name is required');
      return;
    }
    const payload = {
      ...formData,
      estimated_effort_hours: formData.estimated_effort_hours === '' ? null : formData.estimated_effort_hours,
    };
    onSave(payload);
  };

  const renderParameterInput = (param) => {
    const value = formData.parameters[param.name] ?? '';

    switch (param.data_type) {
      case 'integer':
        return (
          <TextField
            key={param.name}
            type="number"
            label={param.display_name || param.name}
            value={value}
            onChange={(e) => handleParameterChange(param.name, parseInt(e.target.value) || 0)}
            inputProps={{ min: param.min_value || 1, max: param.max_value || 10, step: 1 }}
            helperText={param.description}
            fullWidth
            required={param.is_required}
          />
        );

      case 'float':
        return (
          <TextField
            key={param.name}
            type="number"
            label={param.display_name || param.name}
            value={value}
            onChange={(e) => handleParameterChange(param.name, parseFloat(e.target.value) || 0)}
            inputProps={{ min: param.min_value || 0, max: param.max_value || 10, step: 0.1 }}
            helperText={param.description}
            fullWidth
            required={param.is_required}
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            key={param.name}
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => handleParameterChange(param.name, e.target.checked)}
              />
            }
            label={param.display_name || param.name}
          />
        );

      case 'select':
        return (
          <FormControl key={param.name} fullWidth required={param.is_required}>
            <InputLabel>{param.display_name || param.name}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleParameterChange(param.name, e.target.value)}
              label={param.display_name || param.name}
            >
              {param.options.map((option, idx) => (
                <MenuItem key={idx} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return (
          <TextField
            key={param.name}
            label={param.display_name || param.name}
            value={value}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            helperText={param.description}
            fullWidth
            required={param.is_required}
          />
        );
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Project Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={6}
            fullWidth
            helperText="Full description is only shown here in edit mode, not on the project card."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="on_hold">On Hold</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            options={availableSkills}
            value={formData.required_skills}
            onChange={(event, newValue) => handleChange('required_skills', newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Required Skills" placeholder="Select skills" />
            )}
          />
        </Grid>

        {/* Source / tracking fields */}
        <Grid item xs={12}>
          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              Source & Tracking
            </Typography>
          </Divider>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Owner</InputLabel>
            <Select
              value={formData.owner_id ?? ''}
              onChange={(e) => handleChange('owner_id', e.target.value || null)}
              label="Owner"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Client</InputLabel>
            <Select
              value={formData.client_id ?? ''}
              onChange={(e) => handleChange('client_id', e.target.value || null)}
              label="Client"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.acronym} — {c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Estimated Effort (hours)"
            type="number"
            value={formData.estimated_effort_hours}
            onChange={(e) => handleChange('estimated_effort_hours', e.target.value === '' ? '' : parseFloat(e.target.value))}
            inputProps={{ min: 0, step: 0.5 }}
            fullWidth
            helperText="Billed hours. Schedule window = 2× effort ÷ 8 h/day."
          />
        </Grid>

        <Grid item xs={12} md={6} />

        <Grid item xs={12}>
          <TextField
            label="GitLab Issue URL"
            value={formData.gitlab_url}
            onChange={(e) => handleChange('gitlab_url', e.target.value)}
            fullWidth
            placeholder="https://gitlab.example.com/group/project/-/issues/145"
            helperText="Displayed as 'CLR 145' on the project card when a client is set."
          />
        </Grid>

        {parameters.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Project Parameters
            </Typography>
            <Grid container spacing={2}>
              {parameters.map((param) => (
                <Grid item xs={12} md={6} key={param.name}>
                  {renderParameterInput(param)}
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {project ? 'Update' : 'Create'} Project
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProjectForm;
